import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { setLocale, currentLocale } from '@/i18n'

const THEME_KEY = 'iris.theme'

/**
 * Preferencias de interfaz. Deliberadamente pequeño: tema, idioma y estado del
 * menú lateral. Nada de datos de negocio aquí.
 *
 * El tema es una sola fuente de verdad — la clase `dark` en `<html>` — que
 * gobierna a la vez las utilidades de Tailwind y las variables de ElementPlus.
 */
export const useUiStore = defineStore('ui', () => {
  /** `light` | `dark` | `system` */
  const themePreference = ref(readStoredTheme())
  const systemPrefersDark = ref(matchDark()?.matches ?? false)
  const sidebarOpen = ref(false)

  const isDark = computed(() =>
    themePreference.value === 'system' ? systemPrefersDark.value : themePreference.value === 'dark',
  )

  function applyTheme() {
    const root = document.documentElement
    root.classList.toggle('dark', isDark.value)
    root.dataset.theme = isDark.value ? 'dark' : 'light'
  }

  function setTheme(preference) {
    themePreference.value = preference
    try {
      if (preference === 'system') localStorage.removeItem(THEME_KEY)
      else localStorage.setItem(THEME_KEY, preference)
    } catch {
      /* almacenamiento no disponible */
    }
  }

  function toggleTheme() {
    setTheme(isDark.value ? 'light' : 'dark')
  }

  function setLanguage(locale) {
    setLocale(locale)
  }

  const language = computed(() => currentLocale())

  function toggleSidebar(value) {
    sidebarOpen.value = value ?? !sidebarOpen.value
  }

  watch(isDark, applyTheme, { immediate: true })

  // El listener del sistema se instala una vez y vive lo que vive la aplicación.
  matchDark()?.addEventListener?.('change', (event) => {
    systemPrefersDark.value = event.matches
  })

  return {
    themePreference,
    isDark,
    language,
    sidebarOpen,
    setTheme,
    toggleTheme,
    setLanguage,
    toggleSidebar,
  }
})

function matchDark() {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null
}

function readStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_KEY)
    return stored === 'dark' || stored === 'light' ? stored : 'system'
  } catch {
    return 'system'
  }
}
